<template>
  <div class="modal fade" :id="id" tabindex="-1">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ $t('events.send_message') }}</h5>
          <div class="btn btn-icon btn-sm btn-active-light-primary ms-2" @click="closeModal">
              <span class="svg-icon svg-icon-1">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <rect opacity="0.5" x="6" y="17.3137" width="16" height="2" rx="1" transform="rotate(-45 6 17.3137)"
                        fill="currentColor" />
                  <rect x="7.41422" y="6" width="16" height="2" rx="1" transform="rotate(45 7.41422 6)"
                        fill="currentColor" />
                </svg>
              </span>
          </div>
        </div>
        <div class="modal-body">
          <RequiredText />
          <form class="form">
            <div class="card shadow-sm p-5 mb-5">
              <div class="row mb-5">
                <Input
                  container-class="col-12 col-md-6 col-lg-6 "
                  :name="`phone_`+id"
                  type="tel"
                  :label="$t('events.phone')"
                  labelClass="form-label fs-6 text-black"
                  :inputClass="'form-control form-control-lg form-control-solid'"
                  size="lg"
                  :isRequired="true" 
                  v-model="invitation.phone"
                  placeholder="+229 00 00 00 00"
                />
                <Select2
                    :parent-id="'#'+id"
                    :is-required="false"
                    container-class="col-12 col-md-6 col-lg-6"
                    :multiple="false"
                    :show-search="true"
                    :selectClass="'form-select form-control-lg form-select-solid'"
                    :name="'gender'+id"
                    :label="$t('events.gender')"
                    label-class="form-label fs-6 text-black"
                    :placeholder-text="$t('user.civility')"
                    v-model="invitation.gender"
                >
                  <option value="female">{{ $t('user.female') }}</option>
                  <option value="male">{{ $t('user.male') }}</option>
                </Select2>
              </div>

              <div class="row mb-5">
                <Input
                  container-class="col-12 col-md-6 col-lg-6 form-group"
                  :name="`event_lastname_`+id"
                  type="text"
                  :label="$t('events.lastname')"
                  labelClass="form-label fs-6 text-black"
                  :inputClass="'form-control form-control-lg form-control-solid'"
                  size="lg"
                  :isRequired="false" 
                  v-model="invitation.lastname"
                  :placeholder="$t('events.lastname')"
                />
                <Input
                  container-class="col-12 col-md-6 col-lg-6 form-group"
                  :name="`firstname_`+id"
                  type="text"
                  :label="$t('events.firstname')"
                  labelClass="form-label fs-6 text-black"
                  :inputClass="'form-control form-control-lg form-control-solid'"
                  size="lg"
                  :isRequired="false" 
                  v-model="invitation.firstname"
                  :placeholder="$t('events.firstname')"
                />
              </div>
              <div class="d-flex justify-content-end">
                <Button btn-class="btn-sm" type="submit" @click.prevent="addInvitationWhatsapp" id="submit_btn" btn-text="events.add_invitation" size="7px 30px"></Button>
              </div>
            </div>
          </form>

          <div class="table-responsive" style="max-height: 200px; overflow-y: auto;">
            <table class="table align-middle table-row-bordered fs-6 gy-3 gx-4">
              <thead style="background-color: #F2F3F4;">
                <tr>
                  <th>{{$t('events.gender')}}</th>
                  <th>{{$t('events.fullname')}}</th>
                  <th>{{$t('events.phone')}}</th>
                  <th class="w-10px">{{$t('events.actions')}}</th>
                </tr>
              </thead>
              <tbody v-if="formData.invitation?.length > 0">
                <tr v-for="(inv, key) in formData.invitation" :key="key">
                  <td>{{inv.gender ? (inv.gender == 'female' ? $t('user.female') : $t('user.male')) : '...'}}</td>
                  <td>{{(inv.firstname || inv.lastname) ? (inv.firstname + ' ' + inv.lastname) : '...'}}</td>
                  <td>{{inv.phone}}</td>
                  <td class="w-10px">
                    <i class="bi bi-trash-fill fs-2" style="cursor: pointer;" @click.prevent="removeInvitation(key)"></i>
                  </td>
                </tr>
              </tbody>
              <tbody v-else>
                <tr>
                  <td colspan="4">
                    <div class="h-100px d-flex justify-content-center align-items-center" >
                      <div class=" text-center ">
                        <div class="pt-8 ">
                          <Img filename="empty.png" alt=""
                                class="mw-100 h-75px" />
                        </div>
                        <h4 class="fs-2x fw-bolder mb-0">{{ $t('events.notFound') }}</h4> 
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div class="modal-footer">
          <Button type="cancel" id="cancel_btn" btn-class="btn-sm me-3" size="7px 30px" btn-text="button.cancel" @click="closeModal"></Button>
          <Button btn-class="btn-sm" type="submit" id="submit_btn" btn-text="button.send" size="7px 30px" @click.prevent="submitForm" :is-loading="isLoading"></Button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import {ref, defineProps, computed, onMounted} from "vue";
  import {useStore} from "vuex";
  import Button from "@/components/Button.vue"
  import RequiredText from "@/components/RequiredText.vue";
  import Img from "@/components/Img.vue";
  import {useRoute} from "vue-router";
  import Input from "@/components/Input.vue";
  import Select2 from "@/components/Select2.vue";

  const store = useStore()
  const route = useRoute()

  const emits = defineEmits(["load"])

  const props = defineProps({
    id: {
      type: String,
      required: true
    },
    form: {
      type: Object,
      default: {
        event_id: '',
        invitation: []
      }
    },
  })

  const isLoading = ref(false)
  const formData = ref(props.form)
  const invitation = ref({
    phone: '',
    gender: '',
    firstname: '',
    lastname: ''
  })
  
  function closeModal() {
    $('#'+props.id).modal('toggle')
  }

  function addInvitationWhatsapp() {
    if(invitation.value.phone) {
      formData.value.invitation.push(invitation.value)

      invitation.value = {
        phone: '',
        gender: '',
        firstname: '',
        lastname: ''
      }
    }
  }

  function removeInvitation(key) {
    formData.value.invitation.splice(key, 1);
  }

  function submitForm() {
    isLoading.value = true;
    formData.value.event_id = route.params.id
    try {
      let data = {
        event_id: route.params.id,
        invitation: []
      }
      if(formData.value.invitation?.length == 0){
        data.invitation.push(invitation.value)
      }else{
        data.invitation = formData.value.invitation
      }
      store.dispatch('events/sendWhatsappInvtation', data).then((res) =>{
        isLoading.value = false;
        formData.value.invitation = []
        emits('load')
        closeModal();
        window.toastr['success'](i18n.global.t('events.send_mail_successfully'))
      }).catch(() => isLoading.value = false)
    }catch (e) {
      isLoading.value = false;
    }
  }

  onMounted(() => {
    store.dispatch('events/loadParticipators', route.params.id)
    store.dispatch('events/loadVariables')
    store.dispatch('mail/loadMailMessage')
  })
</script>
  
<style scoped>
  .icon-wrapper {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .icon-wrapper i {
    position: relative;
    font-size: 1.5em;
  }

  .icon-wrapper .icon-text {
    position: absolute;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 9px;
    color: white;
    margin-top: 8px;
  }
</style>